import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useToast } from '../Toast/ToastProvider';
import './Onboarding.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/**
 * 5-step first-run onboarding wizard.
 *
 * Step 1: Welcome + resume upload (auto-parse → prefill profile)
 * Step 2: Confirm/edit basic info
 * Step 3: Job preferences (titles, location, salary, work auth)
 * Step 4: Pick 3 dream companies (live ATS verifier)
 * Step 5: First queue with 5-10 prefiltered jobs → first apply → confetti
 *
 * On step 5 success, marks profile.is_complete = true and routes to /AutoJobApply.
 */
export default function Onboarding() {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  // Step-1 state
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFilename, setResumeFilename] = useState(null);
  const fileInputRef = useRef(null);

  // Step-2 state — pre-filled from resume parse
  const [profile, setProfile] = useState({
    fullName: '', email: '', phone: '', location: '',
    linkedIn: '', github: '', portfolio: '',
    jobTitle: '', yearsExperience: '',
    skills: '', professionalSummary: '',
    workExperience: [], education: [], projects: [], certifications: '',
  });

  // Step-3 state — preferences
  const [prefs, setPrefs] = useState({
    jobTitlesPref: '',
    workAuthorization: 'US Citizen',
    salaryExpectation: '',
    willingToRelocate: false,
    desiredEmploymentType: 'Full-time',
    // ── Critical auto-apply screening defaults (added 2026-06-28)
    // These get persisted to user_profiles so the apply pipeline can
    // answer the screening questions Greenhouse/Ashby/Lever forms
    // hammer the user with. Sensible defaults for a US-based candidate.
    requiresSponsorship: false,
    isUsCitizen: true,
    openToRemote: true,
    openToHybrid: true,
    openToOnsite: false,
    noticePeriod: 'Immediate',
    highestDegree: 'Bachelor’s Degree',
  });

  // Step-4 state — companies
  const [companyInput, setCompanyInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [companies, setCompanies] = useState([]); // [{ slug, platform, jobCount }]
  const [companyError, setCompanyError] = useState('');

  // Step-5 state — first batch
  const [firstBatch, setFirstBatch] = useState([]);
  const [appliedJobId, setAppliedJobId] = useState(null);
  const [confetti, setConfetti] = useState(false);

  const token = useCallback(() => localStorage.getItem('token'), []);

  // Auth gate — bounce to home if not logged in
  useEffect(() => {
    if (!loading && !user) navigate('/', { replace: true });
  }, [user, loading, navigate]);

  // ── Step 1: resume upload + parse ──
  const onResumeDrop = async (file) => {
    if (!file) return;
    setResumeFile(file);
    setError('');
    setWorking(true);
    try {
      const form = new FormData();
      form.append('resume', file);
      const r = await fetch(`${API_BASE_URL}/api/profile/parse-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || 'Parse failed');
      setResumeFilename(data.resumeFilename);
      setProfile(prev => ({ ...prev, ...data.data }));
      // Pre-seed prefs job titles from parsed jobTitle
      setPrefs(prev => ({
        ...prev,
        jobTitlesPref: data.data?.jobTitle || prev.jobTitlesPref,
      }));
      setStep(2);
    } catch (e) {
      setError(`Couldn't parse that resume: ${e.message}. Try a different PDF/DOCX.`);
    } finally {
      setWorking(false);
    }
  };

  // ── Step 4: verify a company slug across all ATSes ──
  const verifyCompany = async () => {
    const slug = companyInput.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');
    if (!slug) return;
    if (companies.find(c => c.slug === slug)) {
      setCompanyError(`${slug} already added`);
      return;
    }
    setVerifying(true);
    setCompanyError('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/auto-apply/onboarding/verify-company?slug=${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await r.json();
      if (data.found) {
        setCompanies(prev => [...prev, { slug, platform: data.platform, jobCount: data.jobCount }]);
        setCompanyInput('');
      } else {
        setCompanyError(data.message || `${slug} not found on any supported ATS`);
      }
    } catch (e) {
      setCompanyError(`Verification failed: ${e.message}`);
    } finally {
      setVerifying(false);
    }
  };

  const removeCompany = (slug) => {
    setCompanies(prev => prev.filter(c => c.slug !== slug));
  };

  // ── Step 4 → Step 5: save profile + trigger first discovery ──
  const finishStep4AndDiscover = async () => {
    setWorking(true);
    setError('');
    try {
      // 1. Save profile (includes job_title from prefs.jobTitlesPref + screening fields)
      const saveBody = {
        ...profile,
        jobTitle: prefs.jobTitlesPref,                 // server.js stores this in user_profiles.job_title
        resumeFilename,
        workAuthorization:    prefs.workAuthorization,
        salaryExpectation:    prefs.salaryExpectation,
        willingToRelocate:    prefs.willingToRelocate,
        desiredEmploymentType: prefs.desiredEmploymentType,
        // Critical auto-apply screening fields — line up with the 25
        // user_profiles columns added 2026-06-27. POST /api/profile/save
        // persists each of these; the apply pipeline reads them via
        // loadProfileFromDB and uses them in the combobox classifier
        // + LLM filler to answer Yes/No screening questions
        // consistently across every ATS form.
        requiresSponsorship: prefs.requiresSponsorship,
        isUsCitizen:         prefs.isUsCitizen,
        openToRemote:        prefs.openToRemote,
        openToHybrid:        prefs.openToHybrid,
        openToOnsite:        prefs.openToOnsite,
        noticePeriod:        prefs.noticePeriod,
        highestDegree:       prefs.highestDegree,
      };
      const saveRes = await fetch(`${API_BASE_URL}/api/profile/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(saveBody),
      });
      if (!saveRes.ok) {
        const e = await saveRes.json();
        throw new Error(e.message || 'Profile save failed');
      }

      // 2. Persist user-curated company slugs into the auto-apply preferences
      // so subsequent discovery uses them. We split by platform.
      // (Future enhancement: a real user_companies table. For now, query string
      // is fine since these are read at refresh time from preferences.)
      // [Skipped — discovery uses defaults; companies array is purely informational
      //  in onboarding. Users can curate later via a settings page.]

      // 3. Trigger discovery for the first batch
      const discRes = await fetch(`${API_BASE_URL}/api/auto-apply/queue/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const discData = await discRes.json();
      if (!discRes.ok) throw new Error(discData.message || 'Discovery failed');

      // 4. Pull the top 10 waiting jobs by match score
      const queueRes = await fetch(`${API_BASE_URL}/api/auto-apply/queue?status=waiting`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const queueData = await queueRes.json();
      setFirstBatch((queueData.jobs || []).slice(0, 10));

      setStep(5);
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  // ── Step 5: approve a single job → confetti → done ──
  const approveJob = async (jobId) => {
    setWorking(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/auto-apply/queue/${jobId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || 'Approve failed');
      }
      setAppliedJobId(jobId);
      setConfetti(true);
      toast.success("🎉 Your first application is in flight!", { duration: 5000 });
      // Stop confetti after 6 seconds
      setTimeout(() => setConfetti(false), 6000);
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  const finish = () => {
    navigate('/AutoJobApply');
  };

  if (loading) return <div className="ob-shell"><div className="ob-card">Loading…</div></div>;
  if (!user) return null;

  return (
    <div className="ob-shell">
      {confetti && <ConfettiOverlay />}

      {/* Stepper */}
      <div className="ob-stepper">
        {/* Step 4 (Companies) was removed — discovery uses ~50 default
            tenants, users can add custom ones from Settings later.
            Stepper now shows 1→2→3→5 mapped down to 4 visible dots. */}
        {[
          { n: 1, label: 'Resume',      stepValue: 1 },
          { n: 2, label: 'Profile',     stepValue: 2 },
          { n: 3, label: 'Preferences', stepValue: 3 },
          { n: 4, label: 'First apply', stepValue: 5 },
        ].map(({ n, label, stepValue }) => (
          <div key={n} className={`ob-step ${step === stepValue ? 'active' : step > stepValue ? 'done' : ''}`}>
            <div className="ob-step-dot">{step > stepValue ? '✓' : n}</div>
            <div className="ob-step-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="ob-card">

        {/* ─── STEP 1 — RESUME ─── */}
        {step === 1 && (
          <>
            <h1 className="ob-title">Let's get you applying.</h1>
            <p className="ob-subtitle">Drop your resume — we'll fill out your profile from it.</p>

            <div
              className={`ob-dropzone ${resumeFile ? 'has-file' : ''} ${working ? 'busy' : ''}`}
              onClick={() => !working && fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); }}
              onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) onResumeDrop(e.dataTransfer.files[0]); }}
            >
              <div className="ob-dropzone-icon">{working ? '⏳' : resumeFile ? '✅' : '📄'}</div>
              <div className="ob-dropzone-title">
                {working ? 'Reading your resume…' : resumeFile ? resumeFile.name : 'Drop your resume here'}
              </div>
              <div className="ob-dropzone-hint">
                {working ? 'Terview AI is extracting your details' : 'PDF or DOCX · or click to browse'}
              </div>
              <input
                ref={fileInputRef} type="file" accept=".pdf,.doc,.docx"
                style={{ display: 'none' }}
                onChange={e => onResumeDrop(e.target.files[0])}
              />
            </div>

            {error && <div className="ob-error">{error}</div>}
          </>
        )}

        {/* ─── STEP 2 — CONFIRM PROFILE ─── */}
        {step === 2 && (
          <>
            <h1 className="ob-title">Looks good?</h1>
            <p className="ob-subtitle">
              We pulled this from your resume. Edit anything that's wrong.
            </p>

            <div className="ob-grid">
              <Field label="Full name" required
                value={profile.fullName}
                onChange={v => setProfile(p => ({ ...p, fullName: v }))} />
              <Field label="Email" required
                value={profile.email}
                onChange={v => setProfile(p => ({ ...p, email: v }))} />
              <Field label="Phone" required
                value={profile.phone}
                onChange={v => setProfile(p => ({ ...p, phone: v }))} />
              <Field label="Location" required
                value={profile.location}
                onChange={v => setProfile(p => ({ ...p, location: v }))} />
              <Field label="Current title"
                value={profile.jobTitle}
                onChange={v => setProfile(p => ({ ...p, jobTitle: v }))} />
              <Field label="Years experience"
                value={profile.yearsExperience}
                onChange={v => setProfile(p => ({ ...p, yearsExperience: v }))} />
            </div>

            <Field label="LinkedIn URL"
              value={profile.linkedIn}
              full
              required
              placeholder="https://linkedin.com/in/your-name"
              onChange={v => setProfile(p => ({ ...p, linkedIn: v }))} />

            <Field label="GitHub URL (optional)"
              value={profile.github}
              full
              placeholder="https://github.com/your-username"
              onChange={v => setProfile(p => ({ ...p, github: v }))} />

            <Field label="Skills (comma-separated)"
              value={profile.skills}
              full
              onChange={v => setProfile(p => ({ ...p, skills: v }))} />

            {/* Required to apply — most ATS forms reject a blank contact
                field or LinkedIn, and we won't fabricate them, so collect
                them here once. */}
            {(() => {
              const isLinkedInValid = /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/in\/[^/?#\s]{2,}/i
                .test((profile.linkedIn || '').trim().replace(/^(?!https?:\/\/)/i, 'https://'));
              const missing = [];
              if (!profile.fullName?.trim()) missing.push('full name');
              if (!profile.email?.trim())    missing.push('email');
              if (!profile.phone?.trim())    missing.push('phone');
              if (!profile.location?.trim()) missing.push('location');
              if (!profile.linkedIn?.trim()) missing.push('LinkedIn URL');
              else if (!isLinkedInValid)     missing.push('a valid LinkedIn URL');
              const canContinue = missing.length === 0;
              return (
                <>
                  {!canContinue && (
                    <p className="ob-hint" style={{ color: '#b45309', marginTop: 12 }}>
                      To apply reliably, please add: {missing.join(', ')}.
                    </p>
                  )}
                  <div className="ob-actions">
                    <button className="ob-btn-secondary" onClick={() => setStep(1)}>← Back</button>
                    <button className="ob-btn-primary"
                      disabled={!canContinue}
                      onClick={() => {
                        // Persist the LinkedIn URL WITH a scheme so the ATS
                        // filler types a clickable link, not a bare domain.
                        const li = (profile.linkedIn || '').trim();
                        const normalized = /^https?:\/\//i.test(li) ? li : 'https://' + li;
                        if (normalized !== profile.linkedIn) {
                          setProfile(p => ({ ...p, linkedIn: normalized }));
                        }
                        setStep(3);
                      }}>
                      Continue →
                    </button>
                  </div>
                </>
              );
            })()}
          </>
        )}

        {/* ─── STEP 3 — PREFERENCES ─── */}
        {step === 3 && (
          <>
            <h1 className="ob-title">What kind of role?</h1>
            <p className="ob-subtitle">
              These help us match jobs and answer screening questions for you.
            </p>

            <Field
              label="Target job titles (comma-separated — these become your search keywords)"
              value={prefs.jobTitlesPref}
              full
              placeholder="e.g. software engineer, backend developer, full stack engineer"
              onChange={v => setPrefs(p => ({ ...p, jobTitlesPref: v }))} />

            <div className="ob-grid">
              <SelectField label="Work authorization (US)"
                value={prefs.workAuthorization}
                onChange={v => setPrefs(p => ({ ...p, workAuthorization: v }))}
                options={['US Citizen', 'Green Card', 'H1B Visa', 'OPT / CPT', 'TN Visa', 'E3 Visa', 'Require Sponsorship']} />
              <SelectField label="Employment type"
                value={prefs.desiredEmploymentType}
                onChange={v => setPrefs(p => ({ ...p, desiredEmploymentType: v }))}
                options={['Full-time', 'Contract (W2)', 'Contract (C2C)', 'Contract (1099)', 'Part-time', 'Any']} />
              <Field label="Salary expectation"
                value={prefs.salaryExpectation}
                placeholder="e.g. $120k or $80/hr"
                onChange={v => setPrefs(p => ({ ...p, salaryExpectation: v }))} />
              <ToggleField label="Willing to relocate"
                value={prefs.willingToRelocate}
                onChange={v => setPrefs(p => ({ ...p, willingToRelocate: v }))} />
              {/* ── Critical screening defaults ───────────────────── */}
              <ToggleField label="Need visa sponsorship?"
                value={prefs.requiresSponsorship}
                onChange={v => setPrefs(p => ({ ...p, requiresSponsorship: v }))} />
              <ToggleField label="US Citizen?"
                value={prefs.isUsCitizen}
                onChange={v => setPrefs(p => ({ ...p, isUsCitizen: v }))} />
              <ToggleField label="Open to Remote"
                value={prefs.openToRemote}
                onChange={v => setPrefs(p => ({ ...p, openToRemote: v }))} />
              <ToggleField label="Open to Hybrid"
                value={prefs.openToHybrid}
                onChange={v => setPrefs(p => ({ ...p, openToHybrid: v }))} />
              <ToggleField label="Open to On-site"
                value={prefs.openToOnsite}
                onChange={v => setPrefs(p => ({ ...p, openToOnsite: v }))} />
              <SelectField label="Notice period"
                value={prefs.noticePeriod}
                onChange={v => setPrefs(p => ({ ...p, noticePeriod: v }))}
                options={['Immediate', '2 weeks', '1 month', '2 months', '3+ months']} />
              <SelectField label="Highest degree"
                value={prefs.highestDegree}
                onChange={v => setPrefs(p => ({ ...p, highestDegree: v }))}
                options={['High School', 'Associate’s Degree', 'Bachelor’s Degree', 'Master’s Degree', 'PhD', 'Other']} />
            </div>

            <div className="ob-actions">
              <button className="ob-btn-secondary" onClick={() => setStep(2)}>← Back</button>
              <button className="ob-btn-primary"
                disabled={!prefs.jobTitlesPref.trim() || working}
                onClick={finishStep4AndDiscover}>
                {working ? 'Finding jobs…' : 'Find my first jobs →'}
              </button>
            </div>
          </>
        )}

        {/* ─── STEP 4 — DREAM COMPANIES — REMOVED 2026-06-28 ───
            Step 3's button now calls finishStep4AndDiscover directly,
            which sets step=5 on success. The block below is gated on
            `false` (never renders) instead of deleted so we can resurrect
            it cleanly if we add the companies step back as optional. */}
        {false && step === 4 && (
          <>
            <h1 className="ob-title">Pick 3 dream companies.</h1>
            <p className="ob-subtitle">
              We'll verify they're on Greenhouse / Lever / Workable / Ashby so we can apply on your behalf.
              Try big-tech (stripe, airbnb, coinbase) or smaller startups (notion, linear, ramp).
            </p>

            <div className="ob-company-input-row">
              <input
                className="ob-input"
                value={companyInput}
                placeholder="company slug (e.g. stripe)"
                onChange={e => setCompanyInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') verifyCompany(); }}
                disabled={verifying}
              />
              <button className="ob-btn-primary" onClick={verifyCompany}
                disabled={verifying || !companyInput.trim()}>
                {verifying ? 'Checking…' : '+ Add'}
              </button>
            </div>
            {companyError && <div className="ob-error">{companyError}</div>}

            {companies.length > 0 && (
              <div className="ob-companies-list">
                {companies.map(c => (
                  <div key={c.slug} className="ob-company-pill">
                    <span className="ob-company-name">{c.slug}</span>
                    <span className="ob-company-meta">
                      ✅ on {c.platform} · {c.jobCount} jobs
                    </span>
                    <button className="ob-pill-x" onClick={() => removeCompany(c.slug)}>×</button>
                  </div>
                ))}
              </div>
            )}

            <p className="ob-hint">
              Don't worry — we also discover from {' '}
              <strong>~50 popular companies by default</strong>. The ones you add are extras you really care about.
            </p>

            {error && <div className="ob-error">{error}</div>}

            <div className="ob-actions">
              <button className="ob-btn-secondary" onClick={() => setStep(3)}>← Back</button>
              <button className="ob-btn-primary"
                onClick={finishStep4AndDiscover}
                disabled={working}>
                {working ? 'Finding jobs…' : 'Find my first jobs →'}
              </button>
            </div>
          </>
        )}

        {/* ─── STEP 5 — FIRST APPLY ─── */}
        {step === 5 && (
          <>
            {!appliedJobId ? (
              <>
                <h1 className="ob-title">Top {firstBatch.length} matches for you.</h1>
                <p className="ob-subtitle">
                  Pick one to apply now — we'll keep going in the background while you go about your day.
                </p>

                {firstBatch.length === 0 ? (
                  <div className="ob-empty-batch">
                    <div className="ob-empty-icon">🔍</div>
                    <div className="ob-empty-title">No live matches today.</div>
                    <div className="ob-empty-hint">
                      Try widening your titles in step 3, or come back tomorrow — companies post daily.
                    </div>
                    <button className="ob-btn-primary" onClick={() => setStep(3)}>← Edit preferences</button>
                  </div>
                ) : (
                  <div className="ob-jobs">
                    {firstBatch.map(j => (
                      <div key={j.id} className="ob-job">
                        <div className={`ob-job-score score-${j.match_score >= 80 ? 'high' : j.match_score >= 60 ? 'mid' : 'low'}`}>
                          {j.match_score ?? '—'}
                        </div>
                        <div className="ob-job-body">
                          <div className="ob-job-title">{j.title}</div>
                          <div className="ob-job-meta">
                            {j.company} · {j.location || '—'} · {j.platform}
                          </div>
                        </div>
                        <button className="ob-btn-primary ob-btn-small"
                          onClick={() => approveJob(j.id)}
                          disabled={working}>
                          {working ? '…' : 'Apply'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {error && <div className="ob-error">{error}</div>}

                <div className="ob-actions">
                  <button className="ob-btn-secondary" onClick={() => setStep(4)}>← Back</button>
                  <button className="ob-btn-text" onClick={finish}>
                    Skip for now → take me to the dashboard
                  </button>
                </div>
              </>
            ) : (
              <div className="ob-success">
                <div className="ob-success-icon">🎉</div>
                <h1 className="ob-title">You applied to your first job.</h1>
                <p className="ob-subtitle">
                  We're submitting now in the background. You don't have to stay on this page —
                  we'll keep finding and applying to matches every hour.
                </p>
                <div className="ob-success-stats">
                  <div className="ob-stat">
                    <div className="ob-stat-num">{firstBatch.length - 1}</div>
                    <div className="ob-stat-label">More jobs waiting in your queue</div>
                  </div>
                  <div className="ob-stat">
                    <div className="ob-stat-num">5/hour</div>
                    <div className="ob-stat-label">Pacing rate (no spam)</div>
                  </div>
                </div>
                <div className="ob-actions" style={{ justifyContent: 'center' }}>
                  <button className="ob-btn-primary ob-btn-large" onClick={finish}>
                    Take me to my queue →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Small inline components ────────────────────────────────────────────────

function Field({ label, value, onChange, placeholder, full, required }) {
  return (
    <label className={`ob-field ${full ? 'ob-field-full' : ''}`}>
      <span className="ob-field-label">
        {label}{required && <span style={{ color: '#e5484d' }}> *</span>}
      </span>
      <input
        className="ob-input"
        value={value || ''}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="ob-field">
      <span className="ob-field-label">{label}</span>
      <select className="ob-input" value={value || ''} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function ToggleField({ label, value, onChange }) {
  return (
    <label className="ob-field">
      <span className="ob-field-label">{label}</span>
      <button
        type="button"
        className={`ob-toggle ${value ? 'on' : ''}`}
        onClick={() => onChange(!value)}
      >
        <span className="ob-toggle-knob" />
        <span className="ob-toggle-text">{value ? 'Yes' : 'No'}</span>
      </button>
    </label>
  );
}

// Tiny dependency-free confetti — just CSS-animated DOM particles.
function ConfettiOverlay() {
  const [particles] = useState(() =>
    Array.from({ length: 80 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.2,
      hue: Math.floor(Math.random() * 360),
      size: 6 + Math.random() * 8,
      duration: 3 + Math.random() * 2.5,
    }))
  );
  return (
    <div className="ob-confetti">
      {particles.map(p => (
        <div
          key={p.id}
          className="ob-confetti-piece"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size * 0.5}px`,
            background: `hsl(${p.hue}, 90%, 60%)`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}
